/**
 * Messaging Selector Utility
 *
 * Import these functions in components to get the active messaging.
 * All messaging is controlled by the ACTIVE_VERSION in messagingConfig.js
 *
 * Usage:
 *   import { getHeadline, getSubheadline } from '../utils/messagingSelector';
 *   const headline = getHeadline();
 */

import { messagingVariations, ACTIVE_VERSION } from '../config/messagingConfig';

/**
 * Get the complete active messaging object
 * @returns {Object} Complete messaging configuration
 */
export const getActiveMessaging = () => {
  // Check URL for ?version=X parameter
  const urlParams = new URLSearchParams(window.location.search);
  const urlVersion = urlParams.get('version');

  // Use URL version if provided, otherwise use config default
  const versionToUse = urlVersion ? `version${urlVersion}` : ACTIVE_VERSION;
  const messaging = messagingVariations[versionToUse];

  if (!messaging) {
    console.error(`Invalid version: ${versionToUse}. Falling back to version1.`);
    return messagingVariations.version1;
  }

  return messaging;
};

/**
 * Get current version number from URL or config
 * @returns {number} Current version number
 */
export const getCurrentVersionNumber = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlVersion = urlParams.get('version');

  if (urlVersion) {
    return parseInt(urlVersion);
  }

  return parseInt(ACTIVE_VERSION.replace('version', ''));
};

/**
 * Get the main headline
 * @returns {string} Main headline text
 */
export const getHeadline = () => {
  return getActiveMessaging().headline;
};

/**
 * Get the subheadline
 * @returns {string} Subheadline text
 */
export const getSubheadline = () => {
  return getActiveMessaging().subheadline;
};

/**
 * Get input field placeholders array
 * @returns {Array<string>} Array of placeholder strings
 */
export const getInputPlaceholders = () => {
  return getActiveMessaging().inputPlaceholders;
};

/**
 * Get journey cards configuration
 * @returns {Object} Journey cards object with card1, card2, card3
 */
export const getJourneyCards = () => {
  return getActiveMessaging().journeyCards;
};

/**
 * Get journey cards as an array (for easy mapping)
 * @returns {Array<Object>} Array of card objects
 */
export const getJourneyCardsArray = () => {
  const cards = getJourneyCards();
  return [cards.card1, cards.card2, cards.card3];
};

/**
 * Get active version ID
 * @returns {string} Active version ID
 */
export const getActiveVersionId = () => {
  return getActiveMessaging().id;
};

/**
 * Get active version name
 * @returns {string} Active version name
 */
export const getActiveVersionName = () => {
  return getActiveMessaging().name;
};

/**
 * Get target audience description
 * @returns {string} Target audience
 */
export const getTargetAudience = () => {
  return getActiveMessaging().targetAudience;
};

/**
 * Debug: Log current active messaging to console
 */
export const debugMessaging = () => {
  const messaging = getActiveMessaging();
  console.log('=== ACTIVE MESSAGING ===');
  console.log('Version:', ACTIVE_VERSION);
  console.log('Name:', messaging.name);
  console.log('Target:', messaging.targetAudience);
  console.log('Headline:', messaging.headline);
  console.log('Subheadline:', messaging.subheadline);
  console.log('Placeholders:', messaging.inputPlaceholders);
  console.log('Cards:', messaging.journeyCards);
  console.log('========================');
};
